import { AsyncPipe, CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { matAddSharp } from '@ng-icons/material-icons/sharp';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DataViewModule } from 'primeng/dataview';
import { ScrollTopModule } from 'primeng/scrolltop';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { BehaviorSubject, catchError, combineLatest, EMPTY, map, Observable, throwError } from 'rxjs';
import { ApiClientService } from '../../../services/apiclient/api-client.service';
import { Rabbit } from '../../../services/apiclient/Rabbit';
import { RabbitCardComponent } from "./components/rabbit-card/rabbit-card.component";

@Component({
  selector: 'app-rabbits-overview',
  imports: [
    AsyncPipe,
    ButtonModule,
    CommonModule,
    DataViewModule,
    NgIconComponent,
    ReactiveFormsModule,
    RouterLink,
    ScrollTopModule,
    SelectModule,
    SkeletonModule,
    RabbitCardComponent
  ],
  providers: [
    provideIcons({
      matAddSharp,
    }),
  ],
  templateUrl: './rabbits-overview.component.html',
  styleUrl: './rabbits-overview.component.css',
})
export class RabbitsOverviewComponent {
  private readonly apiClient = inject(ApiClientService);
  private readonly messageService = inject(MessageService);

  protected readonly nonDeletedRabbits$: Observable<Rabbit[]>;

  protected readonly skeletonsIterationSource = Array(10).fill(0);

  private readonly deletedRabbits$ = new BehaviorSubject(<string[]>[]);

  constructor() {
    const rabbits$ = this.apiClient.getRabbits()
      .pipe(
        takeUntilDestroyed(),
        map(rabbits => this.getRabbitsSortedByDisabledThenByName(rabbits)),
      );

    this.nonDeletedRabbits$ = combineLatest([
      rabbits$,
      this.deletedRabbits$
    ])
      .pipe(
        takeUntilDestroyed(),
        map(([rabbits, deletedRabbits]) => rabbits.filter(rabbit => !deletedRabbits.includes(rabbit.id))),
        catchError((error) => this.handleGetRabbitsError(error)),
      );
  }

  onRabbitDeleted(rabbit: Rabbit): void {
    this.deletedRabbits$.next([...this.deletedRabbits$.value, rabbit.id]);
    this.messageService.add({
      severity: 'success',
      summary: 'Kaninchen gelöscht',
      detail: 'Das Kaninchen wurde erfolgreich gelöscht.',
    });
  }

  private getRabbitsSortedByDisabledThenByName(rabbits: Rabbit[]): Rabbit[] {
    return rabbits.sort((a, b) => {
      const sortingByDisabledResult = a.isDisabled === b.isDisabled ? 0 : a.isDisabled ? 1 : -1;
      if (sortingByDisabledResult !== 0) {
        return sortingByDisabledResult;
      }
      return a.name.localeCompare(b.name);
    });
  }

  private handleGetRabbitsError(error: unknown): Observable<never> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => new Error('An unexpected error occurred'));
    }

    switch (true) {
      case error.status >= 500 && error.status <= 599:
        this.messageService.add({
          severity: 'error',
          summary: 'Serverfehler',
          detail:
            'Beim Abrufen der Einträge ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        });
        break;
      default:
        console.error(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Unerwarteter Fehler',
          detail:
            'Beim Abrufen der Einträge hat der Server eine unerwartete Antwort zurückgegeben.',
        });
        break;
    }

    return EMPTY;
  }
}
